/**
 * Central registry of OAuth scopes supported by the GitBook API.
 *
 * OAuth scopes are client capability ceilings. GitBook permissions remain the source of
 * truth. Every OAuth-authenticated API request must pass BOTH the required scope check
 * (the token was granted this scope) AND the current user/resource permission check (the
 * OAuth-authenticated user still holds the required GitBook permission on the concrete
 * resource). A scope never grants access on its own and never elevates a user beyond their
 * current GitBook permissions.
 *
 * OAuth access is deny-by-default: an API operation is unavailable to OAuth apps unless it
 * explicitly opts in with an `oauth` security requirement listing the required scope(s).
 * Operations without an `oauth` declaration can never be reached with an OAuth token.
 *
 * v1 scopes are account-wide: they are not org-scoped or resource-scoped grants. There is
 * no durable grant entity yet, so consent only validates that requested scopes are known
 * and allowed for the client. Runtime API authorization (scope + permission) is the real
 * security boundary.
 *
 * Scopes map to API operation/resource boundaries, not to individual payload fields,
 * because the API permission layer enforces access at the operation/resource level and
 * does not support field-level permission checks. Scopes are therefore no more granular
 * than what the API can consistently enforce.
 *
 * This registry is a distinct namespace from `APIScope`/`APIIntegrationScope`, which are the
 * scopes an integration requests for itself in its manifest. The two overlap on a few strings
 * (`integration:read`, `openapi:read`, `openapi:write`) and mean different things.
 *
 * This registry is the single source of truth for the supported scope list. The OAuth
 * security scheme in the OpenAPI spec is generated from it through the api-client
 * build pipeline (see scripts/generate-oauth-security-scheme.ts); do not maintain the scope
 * list in YAML by hand.
 */

/**
 * Category used to group scopes in consent UI and documentation.
 */
export type OAuthScopeCategory =
    | 'user'
    | 'organization'
    | 'space'
    | 'change-request'
    | 'site'
    | 'integration'
    | 'openapi';

/**
 * Definition of a single OAuth scope.
 */
export interface OAuthScopeDefinition {
    /** The scope string referenced by OpenAPI operation security declarations, e.g. `site:write`. */
    name: string;
    /** Human-readable description shown in consent UI and generated OpenAPI documentation. */
    description: string;
    /** Resource category this scope belongs to, used to group consent text. */
    category: OAuthScopeCategory;
    /**
     * Whether the scope carries a heightened consent/blast-radius meaning (permissions,
     * members, auth/security, insights, publication, merge). Used to emphasise it in consent UI.
     */
    sensitive?: boolean;
}

/**
 * The supported OAuth scopes for the GitBook API.
 *
 * Keep this list in sync with the OpenAPI operation-level `oauth` security declarations.
 * The spec lint test asserts that every used scope exists here and that every scope is used
 * by at least one OAuth-exposed operation.
 */
export const OAUTH_SCOPES = [
    {
        name: 'user:read',
        description: 'Read your GitBook user profile.',
        category: 'user',
    },

    {
        name: 'organization:read',
        description:
            'Read the organizations you belong to, their settings and metadata, and their AI agent conversations.',
        category: 'organization',
    },
    {
        name: 'organization:write',
        description:
            'Create organizations, and update organization settings and AI agent conversations.',
        category: 'organization',
    },
    {
        name: 'organization:members:read',
        description: 'Read organization members, teams, invites and link invites.',
        category: 'organization',
        sensitive: true,
    },
    {
        name: 'organization:members:write',
        description: 'Manage organization members, teams, invites and link invites.',
        category: 'organization',
        sensitive: true,
    },
    {
        name: 'organization:ask',
        description: "Ask AI questions across an organization's content.",
        category: 'organization',
    },
    {
        name: 'organization:search',
        description: "Search across an organization's content.",
        category: 'organization',
    },

    // Collections are being deprecated, so they get no dedicated scope. Collection operations
    // are covered by the space scopes (`space:read`/`space:write` and `space:permissions:*`).
    {
        name: 'space:read',
        description:
            'Read spaces and their content, including pages, change requests and their reviews, comments, files and revision history.',
        category: 'space',
    },
    {
        name: 'space:write',
        description:
            'Create, update and delete spaces and their content, including change requests (create, edit, review), comments, and content imports and exports. Merging a change request also requires change-request:merge.',
        category: 'space',
    },
    {
        name: 'space:permissions:read',
        description: 'Read the users and teams who have access to spaces and collections.',
        category: 'space',
        sensitive: true,
    },
    {
        name: 'space:permissions:write',
        description:
            'Manage who has access to spaces and collections, including inviting users and teams.',
        category: 'space',
        sensitive: true,
    },

    // Reading and editing change requests are authorized at the space level (the same
    // `view`/`edit` permission as space content), so they use `space:read`/`space:write`
    // rather than dedicated change-request scopes. Only merging is a distinct boundary.
    {
        name: 'change-request:merge',
        description:
            'Merge change requests, publishing their changes to the space. Creating, editing and reviewing change requests only requires space:write.',
        category: 'change-request',
        sensitive: true,
    },

    {
        name: 'site:read',
        description:
            'Read sites and their settings, including navigation structure, linked spaces, customization, redirects, topics, AI configuration and documentation issues.',
        category: 'site',
    },
    {
        name: 'site:write',
        description:
            'Create, update and delete sites and their settings, including navigation structure (sections, linked spaces, external links), customization, redirects, topics and AI configuration.',
        category: 'site',
    },
    {
        name: 'site:publish',
        description: 'Publish and unpublish sites, controlling their public exposure.',
        category: 'site',
        sensitive: true,
    },
    {
        name: 'site:permissions:read',
        description: 'Read the users and teams who have access to sites.',
        category: 'site',
        sensitive: true,
    },
    {
        name: 'site:permissions:write',
        description: 'Manage who has access to sites, including inviting users and teams.',
        category: 'site',
        sensitive: true,
    },
    {
        name: 'site:auth:read',
        description:
            'Read how visitors authenticate on a site and its security configuration, including private share links.',
        category: 'site',
        sensitive: true,
    },
    {
        name: 'site:auth:write',
        description:
            'Manage how visitors authenticate on a site and its security configuration, including private share links.',
        category: 'site',
        sensitive: true,
    },
    {
        name: 'site:insights:read',
        description:
            'Read site analytics and insights, including the questions visitors asked Ask AI and the answers they received.',
        category: 'site',
        sensitive: true,
    },
    {
        name: 'site:ask',
        description: "Ask AI questions across a site's content.",
        category: 'site',
    },
    {
        name: 'site:search',
        description: "Search across a site's content.",
        category: 'site',
    },

    {
        name: 'integration:read',
        description: 'Read integrations, their status and where they are installed.',
        category: 'integration',
    },
    {
        name: 'integration:installations:read',
        description:
            'Read integration installations and their configuration, which can include credentials for third-party services.',
        category: 'integration',
        sensitive: true,
    },
    {
        name: 'integration:installations:write',
        description:
            'Install and configure integrations on an organization, its spaces and its sites.',
        category: 'integration',
        sensitive: true,
    },
    {
        name: 'integration:publish',
        description:
            'Publish integrations, which can request their own permissions on your content, and route their events to a development tunnel.',
        category: 'integration',
        sensitive: true,
    },
    {
        name: 'integration:delete',
        description:
            'Unpublish integrations, permanently deleting them and every installation of them.',
        category: 'integration',
        sensitive: true,
    },

    {
        name: 'openapi:read',
        description: 'Read OpenAPI specifications.',
        category: 'openapi',
    },
    {
        name: 'openapi:write',
        description: 'Create, update and delete OpenAPI specifications.',
        category: 'openapi',
    },
] as const satisfies readonly OAuthScopeDefinition[];

/**
 * Union type of all supported OAuth scope names.
 */
export type OAuthScope = (typeof OAUTH_SCOPES)[number]['name'];

/**
 * Set of all supported OAuth scope names, for fast membership checks.
 */
const OAUTH_SCOPE_NAMES: ReadonlySet<string> = new Set(OAUTH_SCOPES.map((scope) => scope.name));

/**
 * Check whether a string is a known/supported OAuth scope.
 */
export function isKnownOAuthScope(scope: string): scope is OAuthScope {
    return OAUTH_SCOPE_NAMES.has(scope);
}

/**
 * Parse a space-delimited OAuth scope string (or array) into a list of scope strings.
 * Returns an empty array for null/undefined/empty input. Does not validate the scopes.
 */
export function parseOAuthScopes(input: string | string[] | null | undefined): string[] {
    if (!input) {
        return [];
    }

    const parts = Array.isArray(input) ? input : input.trim().split(/\s+/);
    return parts.filter(Boolean);
}

/**
 * Assert that every scope in the list is a known/supported OAuth scope.
 * Throws an Error listing the unknown scopes otherwise.
 */
export function assertKnownOAuthScopes(scopes: string[]): void {
    const unknown = scopes.filter((scope) => !isKnownOAuthScope(scope));
    if (unknown.length > 0) {
        throw new Error(`Unknown OAuth scope(s): ${unknown.join(', ')}`);
    }
}

/**
 * Build the `scopes` map (scope name -> description) for the OpenAPI OAuth security scheme.
 * Used by the api-client build pipeline to generate the OAuth security scheme from this registry.
 */
export function getOAuthScopesForOpenAPI(): Record<string, string> {
    return Object.fromEntries(OAUTH_SCOPES.map((scope) => [scope.name, scope.description]));
}
